import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { CreateListViewPayload, UpdateListViewPayload } from '../types/api';

interface UseListViewsOptions {
    userId: number;
    listName: string;
    token: string;
    enabled?: boolean;
}

export const useListViews = ({ userId, listName, token, enabled = true }: UseListViewsOptions) => {
    const queryClient = useQueryClient();

    // Fetch list views for the user
    const {
        data: listViews = [],
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: ['listViews', userId, listName],
        queryFn: () => apiService.getListViewsByOwner(userId, listName, token),
        enabled: enabled && !!userId && !!token,
    });

    // Separate private and public views
    const privateViews = listViews.filter(view => view.type === 'private' && view.owner_id === userId);
    const publicViews = listViews.filter(view => view.type === 'public');

    // Find the default view (public view with is_default === true)
    const defaultView = publicViews.find(view => view.data.is_default === true) || null;

    // Create list view mutation
    const createMutation = useMutation({
        mutationFn: (payload: CreateListViewPayload) => apiService.createListView(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['listViews', userId, listName] });
        },
    });

    // Update list view mutation
    const updateMutation = useMutation({
        mutationFn: (payload: UpdateListViewPayload) => apiService.updateListView(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['listViews', userId, listName] });
        },
    });

    // Delete list view mutation
    const deleteMutation = useMutation({
        mutationFn: ({ id }: { id: number }) => apiService.deleteListView(id, token),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['listViews', userId, listName] });
        },
    });

    const createListView = useCallback(
        async (payload: Omit<CreateListViewPayload, 'token'>) => {
            return createMutation.mutateAsync({ ...payload, token });
        },
        [createMutation, token]
    );

    const updateListView = useCallback(
        async (payload: Omit<UpdateListViewPayload, 'token'>) => {
            return updateMutation.mutateAsync({ ...payload, token });
        },
        [updateMutation, token]
    );

    const deleteListView = useCallback(
        async (id: number) => {
            return deleteMutation.mutateAsync({ id });
        },
        [deleteMutation]
    );

    return {
        listViews,
        privateViews,
        publicViews,
        defaultView,
        isLoading,
        isError,
        error,
        refetch,
        createListView,
        updateListView,
        deleteListView,
        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
    };
};
